import {
  ApiError,
  type DeviceUsageRow,
  formatMoney,
  PRODUCT_TYPES,
  type ProductListRow,
  type ProductType,
  queryKeys,
  type RenameDeviceResult,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Pencil, Smartphone, X } from 'lucide-react'
import { useId, useMemo, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import {
  CompatibleDevicesEditor,
  useDeviceSuggestions,
} from '../components/compatible-devices-editor.js'
import { DataTable } from '../components/data-table.js'
import { useDebouncedValue } from '../components/image-library-grid.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const PAGE_SIZE = 50

const TYPE_LABEL: Record<ProductType, string> = {
  serialized: 'Tracked by IMEI',
  stock: 'Counted stock',
  plan: 'Service plan',
  service: 'Service',
  sim: 'Tracked by ICCID',
  gift_card: 'Gift card',
  digital_license: 'Digital license',
}

interface Paged<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

const nextPage = <T,>(last: Paged<T>) =>
  last.page * last.pageSize < last.total ? last.page + 1 : undefined

const errorText = (err: unknown) => (err instanceof ApiError ? err.message : undefined)

/**
 * Compatibility: which devices work with which products, plans and services.
 *
 * There is no separate device list — a product's "Compatible devices" tags
 * (the field on the product edit screen) are the data, and both tabs are two
 * views over those tags. Devices are matched ignoring upper/lower case.
 */
export function CompatibilityPage() {
  return (
    <Page>
      <PageHeader
        title="Compatibility"
        description="See which products, SIMs, plans and services work with a device, or which devices work with a product. This uses the Compatible devices tags on each product."
      />
      <Tabs defaultValue="by-device">
        <TabsList>
          <TabsTrigger value="by-device">By device</TabsTrigger>
          <TabsTrigger value="by-product">By product or service</TabsTrigger>
        </TabsList>
        <TabsContent value="by-device">
          <ByDeviceTab />
        </TabsContent>
        <TabsContent value="by-product">
          <ByProductTab />
        </TabsContent>
      </Tabs>
    </Page>
  )
}

/**
 * Search box that lists matching products underneath; picking one reports it.
 * Starts searching at two characters, like the order form's product picker.
 */
function ProductSearch({
  onSelect,
  exclude,
  placeholder,
}: {
  onSelect: (product: ProductListRow) => void
  exclude?: (product: ProductListRow) => boolean
  placeholder: string
}) {
  const [search, setSearch] = useState('')
  const q = useDebouncedValue(search.trim(), 250)
  const { data, isFetching } = useQuery({
    queryKey: queryKeys.products.list({ q, forCompatibility: true }),
    queryFn: () =>
      api.get<Paged<ProductListRow>>('/products', {
        query: { q, isArchived: false, pageSize: 20 },
      }),
    enabled: q.length >= 2,
  })
  const results = (data?.items ?? []).filter((p) => !exclude?.(p))

  return (
    <div className="space-y-2">
      <Input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
      {q.length >= 2 && !isFetching && results.length === 0 ? (
        <p className="text-sm text-foreground-muted">No matching products.</p>
      ) : null}
      {results.length > 0 ? (
        <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
          {results.map((product) => (
            <li key={product.id}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                onClick={() => {
                  onSelect(product)
                  setSearch('')
                }}
              >
                <span>
                  <span className="block font-medium">{product.name}</span>
                  <span className="block font-identifier text-2xs text-foreground-muted">
                    {product.sku}
                  </span>
                </span>
                <Badge>{TYPE_LABEL[product.type]}</Badge>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/* ------------------------------ By device ------------------------------ */

function ByDeviceTab() {
  const [search, setSearch] = useState('')
  const q = useDebouncedValue(search.trim(), 250)
  const [selected, setSelected] = useState<string | null>(null)

  const filters = { q: q || undefined, pageSize: PAGE_SIZE }
  const devices = useInfiniteQuery({
    queryKey: queryKeys.products.devices(filters),
    queryFn: ({ pageParam }) =>
      api.get<Paged<DeviceUsageRow>>('/products/devices', {
        query: { ...filters, page: pageParam },
      }),
    initialPageParam: 1,
    getNextPageParam: nextPage,
  })
  const rows = devices.data?.pages.flatMap((p) => p.items) ?? []
  const total = devices.data?.pages[0]?.total ?? 0

  const columns = useMemo<ColumnDef<DeviceUsageRow, unknown>[]>(
    () => [
      {
        id: 'device',
        header: 'Device',
        cell: ({ row }) => <span className="font-medium">{row.original.device}</span>,
      },
      {
        id: 'productCount',
        header: 'Products',
        cell: ({ row }) => <span className="tabular">{row.original.productCount}</span>,
      },
    ],
    [],
  )

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
      <div className="space-y-3">
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search devices…"
          aria-label="Search devices"
        />
        <DataTable
          data={rows}
          columns={columns}
          isLoading={devices.isLoading}
          getRowId={(row) => row.device.toLowerCase()}
          onRowClick={(row) => setSelected(row.device)}
          density="compact"
          infinite={{
            total,
            hasMore: devices.hasNextPage ?? false,
            isFetchingMore: devices.isFetchingNextPage,
            onLoadMore: () => void devices.fetchNextPage(),
          }}
          empty={
            <EmptyState
              icon={Smartphone}
              title={q ? 'No devices match' : 'No devices tagged yet'}
              description={
                q
                  ? 'Try a different spelling.'
                  : 'Add devices from the By product or service tab, or on any product’s edit screen.'
              }
            />
          }
        />
      </div>

      <div>
        {selected ? (
          <DeviceProducts
            key={selected.toLowerCase()}
            device={selected}
            onRenamed={(to) => setSelected(to)}
          />
        ) : (
          <EmptyState
            icon={Smartphone}
            title="Pick a device"
            description="Choose a device on the left to see every product, SIM, plan and service that works with it."
          />
        )}
      </div>
    </div>
  )
}

function DeviceProducts({
  device,
  onRenamed,
}: {
  device: string
  onRenamed: (to: string) => void
}) {
  const { can } = useAuth()
  const canWrite = can('catalog:write')
  const toast = useToast()
  const queryClient = useQueryClient()
  const [typeFilter, setTypeFilter] = useState<'all' | ProductType>('all')
  const [renaming, setRenaming] = useState(false)

  const filters = {
    deviceExact: device,
    type: typeFilter === 'all' ? undefined : typeFilter,
    pageSize: PAGE_SIZE,
    sort: 'name',
    order: 'asc',
  }
  const list = useInfiniteQuery({
    queryKey: queryKeys.products.list({ ...filters, forCompatibility: true }),
    queryFn: ({ pageParam }) =>
      api.get<Paged<ProductListRow>>('/products', { query: { ...filters, page: pageParam } }),
    initialPageParam: 1,
    getNextPageParam: nextPage,
  })
  const rows = list.data?.pages.flatMap((p) => p.items) ?? []
  const total = list.data?.pages[0]?.total ?? 0

  const toggle = useMutation({
    mutationFn: (v: { id: string; action: 'add' | 'remove' }) =>
      api.post(`/products/${v.id}/compatible-devices`, { action: v.action, device }),
    onSuccess: async (_data, v) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success(v.action === 'add' ? 'Product tagged' : 'Product untagged')
    },
    onError: (err) => toast.error('Could not update the product', errorText(err)),
  })

  const columns = useMemo<ColumnDef<ProductListRow, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Product',
        cell: ({ row }) => (
          <div>
            <span className="block font-medium">{row.original.name}</span>
            <span className="block font-identifier text-2xs text-foreground-muted">
              {row.original.sku}
            </span>
          </div>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => <Badge>{TYPE_LABEL[row.original.type]}</Badge>,
      },
      {
        id: 'category',
        header: 'Category',
        cell: ({ row }) => row.original.category ?? '—',
      },
      {
        id: 'price',
        header: 'Price',
        cell: ({ row }) => <span className="tabular">{formatMoney(row.original.priceCents)}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) =>
          row.original.isArchived ? (
            <Badge>Archived</Badge>
          ) : row.original.isActive ? (
            <Badge tone="success">Active</Badge>
          ) : (
            <Badge tone="warning">Inactive</Badge>
          ),
      },
      ...(canWrite
        ? [
            {
              id: 'untag',
              header: '',
              cell: ({ row }) => (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${device} from ${row.original.name}`}
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate({ id: row.original.id, action: 'remove' })}
                >
                  <X />
                </Button>
              ),
            } satisfies ColumnDef<ProductListRow, unknown>,
          ]
        : []),
    ],
    [canWrite, device, toggle],
  )

  const taggedIds = new Set(rows.map((r) => r.id))

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{device}</h2>
          <p className="text-sm text-foreground-muted">
            {total} {total === 1 ? 'product works' : 'products work'} with this device
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as 'all' | ProductType)}>
            <SelectTrigger className="w-48" aria-label="Filter by type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {PRODUCT_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {TYPE_LABEL[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canWrite ? (
            <Button variant="secondary" onClick={() => setRenaming(true)}>
              <Pencil /> Rename everywhere
            </Button>
          ) : null}
        </div>
      </div>

      {canWrite ? (
        <div className="space-y-1">
          <p className="text-sm font-medium">Add a product to this device</p>
          <ProductSearch
            placeholder="Search a product, SIM, plan or service by name or SKU…"
            exclude={(p) =>
              taggedIds.has(p.id) ||
              p.compatibleDevices.some((d) => d.toLowerCase() === device.toLowerCase())
            }
            onSelect={(p) => toggle.mutate({ id: p.id, action: 'add' })}
          />
        </div>
      ) : null}

      <DataTable
        data={rows}
        columns={columns}
        isLoading={list.isLoading}
        getRowId={(row) => row.id}
        infinite={{
          total,
          hasMore: list.hasNextPage ?? false,
          isFetchingMore: list.isFetchingNextPage,
          onLoadMore: () => void list.fetchNextPage(),
        }}
        empty={
          <EmptyState
            icon={Smartphone}
            title="Nothing is tagged for this device"
            description={
              typeFilter === 'all'
                ? 'No product lists this device as compatible.'
                : 'No product of this type lists this device. Try All types.'
            }
          />
        }
      />

      {renaming ? (
        <RenameDeviceDialog
          device={device}
          productCount={typeFilter === 'all' ? total : undefined}
          onClose={() => setRenaming(false)}
          onRenamed={(to) => {
            setRenaming(false)
            onRenamed(to)
          }}
        />
      ) : null}
    </div>
  )
}

function RenameDeviceDialog({
  device,
  productCount,
  onClose,
  onRenamed,
}: {
  device: string
  productCount: number | undefined
  onClose: () => void
  onRenamed: (to: string) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState(device)
  const listId = useId()
  const suggestions = useDeviceSuggestions()
  const to = name.trim()

  const rename = useMutation({
    mutationFn: () =>
      api.post<RenameDeviceResult>('/products/devices/rename', { from: device, to }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success(
        'Device renamed',
        `${result.updated} ${result.updated === 1 ? 'product' : 'products'} updated`,
      )
      onRenamed(to)
    },
    onError: (err) => toast.error('Could not rename the device', errorText(err)),
  })

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename “{device}” everywhere</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-foreground-muted">
            Changes this device’s name on{' '}
            {productCount === undefined ? 'every product' : `all ${productCount} products`} that
            list it. Use this to fix spelling, or to merge into a name that already exists — a
            product that already has the new name keeps just one.
          </p>
          <Input
            value={name}
            list={listId}
            maxLength={64}
            autoComplete="off"
            aria-label="New device name"
            onChange={(e) => setName(e.target.value)}
          />
          <datalist id={listId}>
            {suggestions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!to || to === device || rename.isPending}
            onClick={() => rename.mutate()}
          >
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* --------------------------- By product/service --------------------------- */

function ByProductTab() {
  const [selectedId, setSelectedId] = useState<string | null>(null)

  return (
    <div className="max-w-2xl space-y-6">
      <ProductSearch
        placeholder="Search a product, SIM, plan or service by name or SKU…"
        onSelect={(p) => setSelectedId(p.id)}
      />
      {selectedId ? (
        <ProductDevices key={selectedId} productId={selectedId} />
      ) : (
        <EmptyState
          icon={Smartphone}
          title="Pick a product or service"
          description="Search above to see, and edit, the devices it works with."
        />
      )}
    </div>
  )
}

function ProductDevices({ productId }: { productId: string }) {
  const { can } = useAuth()
  const canWrite = can('catalog:write')
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data: product, isLoading } = useQuery({
    queryKey: queryKeys.products.detail(productId),
    queryFn: () => api.get<ProductListRow>(`/products/${productId}`),
  })
  const [draft, setDraft] = useState<string[] | null>(null)
  const [editorKey, setEditorKey] = useState(0)

  const save = useMutation({
    mutationFn: (compatibleDevices: string[]) =>
      api.patch<ProductListRow>(`/products/${productId}`, { compatibleDevices }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      setDraft(null)
      setEditorKey((k) => k + 1)
      toast.success('Compatible devices saved')
    },
    onError: (err) => toast.error('Could not save', errorText(err)),
  })

  if (isLoading || !product) return <p className="text-sm text-foreground-muted">Loading…</p>

  const current = draft ?? product.compatibleDevices
  return (
    <div className="space-y-4 rounded-lg border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{product.name}</h2>
          <span className="font-identifier text-2xs text-foreground-muted">{product.sku}</span>
        </div>
        <Badge>{TYPE_LABEL[product.type]}</Badge>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">Compatible devices</p>
        {canWrite ? (
          <>
            <CompatibleDevicesEditor key={editorKey} value={current} onChange={setDraft} />
            <div className="flex gap-2">
              <Button
                disabled={draft === null || save.isPending}
                onClick={() => draft && save.mutate(draft)}
              >
                Save
              </Button>
              <Button
                variant="secondary"
                disabled={draft === null}
                onClick={() => {
                  setDraft(null)
                  setEditorKey((k) => k + 1)
                }}
              >
                Reset
              </Button>
            </div>
          </>
        ) : product.compatibleDevices.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {product.compatibleDevices.map((d) => (
              <Badge key={d}>{d}</Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-foreground-muted">No compatible devices tagged yet.</p>
        )}
      </div>
    </div>
  )
}
